import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { fetchSales, setSalesTarget } from 'api'
import numeral from 'numeral'
import moment from 'moment'

import {
  AverageContractValue,
  AverageInspectionRating,
  ConversionRate,
  DistributionOfRevenue,
  InspectionMetrics,
  SalesByLocation,
  SalesByLocationType,
  SalesLeaderboard,
  SalesTarget,
  SimpleMetricCard,
  Tally,
  TimeBar,
  SkeletonCard,
} from './components'
import { calculateRevenueStats } from './util'

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-auto-rows: auto;
  grid-gap: 1rem;
  margin-top: 0.9375rem;
`

// Define container for dashboard
const DashboardContainer = styled.div`
  padding: 0.5rem 3.75rem;
  display: flex;
  flex-direction: column;
`

// Number of skeleton cards to show
const SKELETON_COUNT = 16

const dateRangeOptions = [
  { name: '1w', startDate: moment().subtract(7, 'days').format() },
  { name: '1m', startDate: moment().subtract(1, 'months').format() },
  { name: '3m', startDate: moment().subtract(3, 'months').format() },
  { name: '6m', startDate: moment().subtract(6, 'months').format() },
  { name: '1y', startDate: moment().subtract(1, 'years').format() },
  { name: 'All', startDate: null },
]

const SalesDashboard = () => {
  const [currentTimeInterval, setCurrentTimeInterval] = useState(5)
  const dispatch = useDispatch()

  // Redux state
  const requesting = useSelector(state => state.sales.requesting)
  const stats = useSelector(state => state.sales.stats)
  const { currentCompany, currentEmployment } = useSelector(state => state.user)

  // Derived revenue stats
  const revenueStats = useMemo(
    () => calculateRevenueStats(stats?.wonProposals),
    [stats]
  )

  // Fetch stats when timeframe changes
  useEffect(() => {
    const start = dateRangeOptions[currentTimeInterval].startDate
    dispatch(fetchSales(start, null))
  }, [dispatch, currentTimeInterval])

  // Handler for timeframe change
  const handleTimeChange = item => {
    const idx = dateRangeOptions.findIndex(d => d.name === item.name)
    if (idx >= 0) setCurrentTimeInterval(idx)
  }

  // Submit sales target
  const handleSalesTargetSubmit = value => dispatch(setSalesTarget({ salesTarget: value }))

  const conversionRateStats = {
    created: stats?.counts?.proposalCount?.created || 0,
    won: stats?.counts?.business?.dealsWon || 0,
  }

  const renderSkeletons = () =>
    Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonCard key={i} />)

  // Extract counts
  const counts = stats?.counts || {}

  return (
    <DashboardContainer>
      <TimeBar
        data={dateRangeOptions}
        defaultValue={dateRangeOptions[currentTimeInterval]}
        onChange={handleTimeChange}
      />
      <StatsContainer>
        {requesting ? (
          renderSkeletons()
        ) : (
          <>
            <SimpleMetricCard
              title="New Leads"
              value={counts.leads}
              tooltipText="This shows the number of unique potential customer locations identified through Walkthroughs or Proposals created during the selected time frame that haven't yet resulted in a won deal."
            />
            <SimpleMetricCard
              title="Missed Opportunities"
              value={counts.declinedProposals}
              tooltipText="This is the count of proposals that were explicitly marked as 'Declined' within the chosen time period, representing potential deals that did not proceed."
            />
            <SimpleMetricCard
              title="Total Sales"
              value={revenueStats?.yearlyStats?.newSales}
              prefix="$"
              tooltipText="This represents the total combined 'Yearly Total Revenue' from all proposals that were either marked as 'Won' or were sent for e-sign and signed by both you and the client within the selected period."
            />
            <ConversionRate
              stats={conversionRateStats}
              tooltipText="This shows the percentage of proposals created during the selected period that resulted in a new deal. A deal is considered 'won' if the proposal was marked as 'Won' or was sent for e-sign and signed by both you and the client."
            />
            <SalesTarget
              monthlyContracts={stats?.monthlyContracts}
              updateSalesTarget={handleSalesTargetSubmit}
              salesTarget={currentCompany?.analyticsSettings?.salesTarget}
              employeeRole={currentEmployment?.role}
              tooltipText="See how the total 'Yearly Revenue' from deals won this calendar month stacks up against your monthly sales goal. A deal is considered 'won' if the proposal was marked as 'Won' or was sent for e-sign and signed by both you and the client."
            />
            <AverageContractValue
              average={revenueStats?.yearlyStats?.averageContractValue}
              count={stats?.wonProposals?.length}
              tooltipText="This is the average 'Yearly Total Revenue' per won deal within the selected period. Deals are considered 'won' if the proposal was marked as 'Won' or was sent for e-sign and signed by both you and the client."
            />
            <SalesByLocation
              key={`sales-location-${currentTimeInterval}`}
              stats={revenueStats?.yearlyStats?.salesByLocation}
              merchantCity={currentCompany?.address?.city}
              tooltipText="Explore where your sales revenue is coming from. This breaks down the Total Sales amount based on the location (City, State, or Country) of the clients whose proposals were won in the selected period. Proposals are considered 'won' if marked as 'Won' or sent for e-sign and signed by both parties."
            />
            <DistributionOfRevenue
              stats={revenueStats?.yearlyStats?.salesByService}
              tooltipText="Understand which services contribute most to your revenue. This chart shows the breakdown of Total Sales by the type of service included in proposals won during the selected period. Proposals are considered 'won' if marked as 'Won' or sent for e-sign and signed by both parties."
            />
            <SalesByLocationType
              key={`sales-location-type-${currentTimeInterval}`}
              stats={revenueStats?.yearlyStats?.salesByLocationType}
              tooltipText="See which types of client locations (like Offices, Churches, etc.) are generating the most revenue. This breaks down Total Sales based on the Location Type associated with proposals won in the selected period. Proposals are considered 'won' if marked as 'Won' or sent for e-sign and signed by both parties."
            />
            <SalesLeaderboard
              key={`sales-leaderboard-${currentTimeInterval}`}
              stats={revenueStats?.yearlyStats?.salesLeaderboard}
              tooltipText="See which team members are bringing in the most revenue. This ranks users based on the total 'Yearly Revenue' from proposals they created that were won during the selected period. Proposals are considered 'won' if marked as 'Won' or sent for e-sign and signed by both parties."
            />
            <InspectionMetrics
              stats={counts.inspectionCount}
              tooltipText="Track the progress of your inspections. This shows how many inspections were created compared to how many were completed (based on their scheduled date) within the selected timeframe. The percentage reflects the completion rate."
            />
            <AverageInspectionRating
              inspections={stats?.completedInspections}
              tooltipText="Gauge inspection quality. This displays an average score based on each inspection's overall recorded percentage."
            />
            <SimpleMetricCard
              title="Total Sq. Ft. Managed"
              value={revenueStats?.yearlyStats?.totalSqFt}
              formatter={val => numeral(val).format('0,0')}
              tooltipText="This shows the total square footage covered by all deals won during the selected period. A deal is considered 'won' if the proposal was marked as 'Won' or was sent for e-sign and signed by both you and the client."
            />
            <Tally
              stats={counts.walkthroughCount}
              title="Walkthrough"
              tooltipText="Track your walkthrough activity. This shows the number of walkthroughs created compared to those completed (based on their scheduled date) within the selected period."
            />
            <Tally
              stats={counts.estimateCount}
              title="Estimates"
              tooltipText="See how many estimates your team created during the selected time frame."
            />
            <Tally
              stats={counts.proposalCount}
              title="Proposals"
              tooltipText="Monitor your proposal pipeline. This compares the number of proposals created to the number actually sent to clients within the selected period."
            />
            <Tally
              stats={counts.business}
              title="New Business"
              tooltipText="Measure new business growth. This compares the number of proposals won (either marked 'Won' or sent for e-sign and signed by both parties) against the number of brand new client accounts created during the selected period."
            />
          </>
        )}
      </StatsContainer>
    </DashboardContainer>
  )
}

export default SalesDashboard
